import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { ImpersonationProvider } from "@/hooks/useImpersonation";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";

// Lazy load non-critical pages
const Auth = lazy(() => import("./pages/Auth"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const Index = lazy(() => import("./pages/Index"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLMS = lazy(() => import("./pages/AdminLMS"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Lesson = lazy(() => import("./pages/Lesson"));
const Complete = lazy(() => import("./pages/Complete"));
const Verify = lazy(() => import("./pages/Verify"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CreatorDashboard = lazy(() => import("./pages/CreatorDashboard"));
const CreatorAuth = lazy(() => import("./pages/CreatorAuth"));
const CreatorForgotPassword = lazy(() => import("./pages/CreatorForgotPassword"));
const CreatorOnboarding = lazy(() => import("./pages/CreatorOnboarding"));
const CreatorLanding = lazy(() => import("./pages/CreatorLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} storageKey="app-theme">
      <TooltipProvider>
        <AuthProvider>
          <ImpersonationProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ImpersonationBanner />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-[#e5e5e5]">Loading...</div>}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/book" element={<BookingPage />} />
                  <Route path="/thank-you" element={<ThankYouPage />} />
                  <Route path="/training" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
                  <Route path="/admin/lms" element={<ProtectedRoute requireAdmin><AdminLMS /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/lesson/:lessonId" element={<ProtectedRoute><Lesson /></ProtectedRoute>} />
                  <Route path="/complete" element={<ProtectedRoute><Complete /></ProtectedRoute>} />
                  <Route path="/verify/:certificateNumber" element={<Verify />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/creator/auth" element={<CreatorAuth />} />
                  <Route path="/creator/forgot-password" element={<CreatorForgotPassword />} />
                  <Route path="/creator/onboarding" element={<ProtectedRoute><CreatorOnboarding /></ProtectedRoute>} />
                  <Route path="/creator" element={<ProtectedRoute><CreatorDashboard /></ProtectedRoute>} />
                  <Route path="/creators" element={<CreatorLanding />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ImpersonationProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
